

















































import { Ref, defineComponent, nextTick, onMounted, onUnmounted, PropType, ref, watchEffect, computed } from '@vue/composition-api';
import CouponItem from './coupon-item.vue';
import { useCouponStore } from '@/store/coupon';
import { storeToRefs } from 'pinia';
import { getElementTop, isScrollToEnd } from '@/utils';
import { useTabStore } from '@/store/tab';
import { throttle } from 'lodash-es';
import ThemeNotice from '../../common/theme-notice.vue';
import { DEFAULT_COUPON_TAB_ITEM } from '@/config';
import { useCouponLock } from '@/hooks/use-coupon-lock';
import CouponDialog from './coupon-dialog/index.vue';
import { DetailDialogType, useCouponTaskDialog } from '@/hooks/use-coupon-task-dialog';
import { useAdBnanner } from '@/hooks/use-ad-banner';
import { getOffsetY } from '@/utils/index';
import { CouponTabType, TabCouponList } from '@/types/tab';
import { ExActAndConfigCouponInfo } from '@/types/coupon';

export default defineComponent({
  name: 'CouponListV3',
  props: {
    categorys: {
      type: Array as PropType<CouponTabType[]>,
      default: () => [],
    },
  },
  components: { CouponItem, ThemeNotice, CouponDialog },

  setup(props) {
    // 分类券列表
    const tabCouponList = ref<TabCouponList[]>([]);
    // 券弹窗
    const couponDialog = ref<InstanceType<typeof CouponDialog> | null>(null);
    // 当前操作的券
    const currentCouponInfo = ref();
    /** 是否展示全局详情弹窗 - 券 */
    const { isShowCouponDetail, showData } = useCouponTaskDialog();

    const couponStore = useCouponStore();
    const { formattedCouponList, couponStateEndList } = storeToRefs(couponStore);
    // 二次处理couponName/goodsUrl，优先使用货源配置的
    const exFormattedCouponList = computed(() => {
      return formattedCouponList.value.map((item) => {
        return Object.assign({}, item, {
          couponName: item.v2couponName || item.couponName,
          // @ts-ignore
          goodsUrl: item.v2goodsUrl || item.goodsUrl,
        });
      });
    });

    const tabStore = useTabStore();
    const { setCurrentTabKey, getTabTips } = tabStore;
    const { currentTabKey } = storeToRefs(tabStore);

    const { isLock } = useCouponLock();
    // 是否滚动中
    const isScrolling = ref(false);
    const scrollTimer = ref(0);
    // 进行滚动
    function onScroll(scrollDistance: number) {
      // 不需要重复滚动
      if (scrollDistance === getOffsetY()) return;
      isScrolling.value = true;
      if (scrollTimer.value) {
        clearTimeout(scrollTimer.value);
        scrollTimer.value = 0;
      }
      window.scrollTo({
        left: 0,
        top: scrollDistance,
        behavior: 'smooth',
      });
      scrollTimer.value = setTimeout(() => {
        isScrolling.value = false;
      }, 1500);
    };
    const { init: initAdBanner } = useAdBnanner();
    // 获取需要滚动的距离
    function getPositionWrapperDistance(
      currentTabKey: string,
      positionWrapper: HTMLElement,
      isScrollToCouponTag: boolean,
      topGap: number,
    ) {
      const top = getElementTop(positionWrapper);
      let scrollDistance = 0;
      if (isScrollToCouponTag) {
        scrollDistance = top - topGap;
      } else {
        // 是否是第一个 tab
        const isFirstTab = props.categorys[0].key === currentTabKey;
        // tab 标题高度
        const tabTitleHeight = document.querySelector(`.tab-wrapper_${currentTabKey}`)?.clientHeight || 0;

        // 判断是否是第一个 tab，如果不是，则多滚动 tab 标题的高度
        scrollDistance = top - topGap + (!isFirstTab ? tabTitleHeight : 0);
      }
      return scrollDistance;
    }
    // 滚动到指定券的位置
    async function scrollToPosition(currentTabKey: string, topGap = 106) {
      if (!currentTabKey) return;
      // 找到需要滚动的元素：可能是券（点击banner 的券或者外部链接带 couponId)，可能是 tab
      let positionWrapper;
      let isScrollToCouponTag = true; // 是否滚动到具体的券
      const hasCouponId = !!(showData.showType === DetailDialogType.coupon && showData.id); // 是否需要滚动到具体的券
      if (hasCouponId) {
        positionWrapper = document.getElementById(`coupon-wrapper_${currentTabKey}_${showData.id}`) as HTMLElement;
      }
      if (!positionWrapper) {
        positionWrapper = document.getElementById(`coupon-wrapper_${currentTabKey}`) as HTMLElement;
        isScrollToCouponTag = false;
      }
      if (!positionWrapper) return;

      // 如果需要滚动到具体的券，需要先初始化顶部广告 banner，不然券获取 scrollTop 不准确
      if (isScrollToCouponTag) {
        await initAdBanner();
      }

      // 滚动的距离
      const scrollDistance = getPositionWrapperDistance(currentTabKey, positionWrapper, isScrollToCouponTag, topGap);

      // 进行滚动
      onScroll(scrollDistance);
    }
    // 初始化各个分类的范围
    const heightList = ref<any[]>([]);
    function initTabRange() {
      const arr: number[] = [];
      for (const iterator of props.categorys) {
        const positionWrapper = document.getElementById(`coupon-wrapper_${iterator.key}`) as HTMLElement;
        if (!positionWrapper) return;
        const top = getElementTop(positionWrapper);
        arr.push(top - 400);
      }
      arr.splice(0, 1, 0);
      arr.push(999999999);
      for (let index = 0; index < arr.length - 1; index++) {
        heightList.value.push({
          key: props.categorys[index].key,
          min: arr[index],
          max: arr[index + 1],
        });
      }
    }
    // 滚动的时候切换 tab
    function handleScrollSwitchTab(scrollTop: number) {
      // tab 点击滚动中，不监听切换 tab，否则 tab 设置动画闪动
      if (isScrolling.value) return;
      if (isScrollToEnd()) {
        const lastTabKey = props.categorys[props.categorys.length - 1].key;
        // 不重复设置 key
        if (currentTabKey.value === lastTabKey) return;
        setCurrentTabKey(lastTabKey);
        return;
      }
      for (const iterator of heightList.value) {
        if (scrollTop >= iterator.min && scrollTop <= iterator.max) {
          // 不重复设置 key
          if (currentTabKey.value === iterator.key) return;
          setCurrentTabKey(iterator.key);
          break;
        }
      }
    }
    // 使用throttle函数包装函数，限制函数执行频率为500ms
    const handleScrollTabThrottle = throttle(handleScrollSwitchTab, 500);

    // 点击券，打开券详情弹窗
    function onCouponClick(couponInfo) {
      currentCouponInfo.value = couponInfo;
      nextTick(() => couponDialog.value?.showCouponInfo());
    }

    // 给券分成两列，采用瀑布流模式（业内 css 无法满足）
    function groupList(key: string) {
      const result: any[][] = [[], []];
      exFormattedCouponList.value
        .filter((ele: { tabKey: string[] }) => (ele.tabKey || []).includes(key))
        .forEach((item, i) => {
          result[i % 2].push(item);
        });
      return result;
    }
    // 格式化各券下的券列表
    function formatTabCouponList() {
      const tabCouponListData: TabCouponList[] = [];
      for (const category of props.categorys) {
        // 将券按照分类分成两列（瀑布流模式）
        const couponList: any[][] = groupList(category.key);
        if (couponList[0].length > 0) {
          tabCouponListData.push({
            couponList,
            tab: category,
          });
        }
      }
      tabCouponList.value = tabCouponListData;
    }
    watchEffect(() => {
      // 监听券列表数据，格式化（兑换后需要刷新数据）
      if (exFormattedCouponList.value.length) {
        console.log('===更新数据了...');
        formatTabCouponList();
      }
    });
    onMounted(() => {
      if (!props.categorys.length) return;
      initTabRange();
      window.addEventListener('scroll', () => {
        const scrollTop = document.documentElement.scrollTop || window.pageYOffset || document.body.scrollTop;
        handleScrollTabThrottle(scrollTop);
      });
      watchEffect(() => {
        isShowCouponDetail((couponId: string) => {
          const item = [
            ...exFormattedCouponList.value,
            ...couponStateEndList.value,
          ].find(coupon => coupon.couponId === couponId);
          if (!item) return;
          onCouponClick(item);
        });
      });
    });
    onUnmounted(() => {
      clearTimeout(scrollTimer.value);
    });
    return {
      DEFAULT_COUPON_TAB_ITEM,
      heightList,
      groupList,
      scrollToPosition,
      getTabTips,
      isLock,
      couponDialog,
      currentCouponInfo,
      onCouponClick,
      tabCouponList,
    };
  },
});
